// firmenbezugNachreichen.ts — geschäftliche Kanäle einer Person an eine Firma
// hängen, die erst beim Speichern entstanden ist (P8, Plan
// 2026-09-30-ocr-scan-kontaktanlage).
//
// Die Maske ordnet einen geschäftlichen Kanal einer Firma zu, auch wenn die
// Firma noch neu ist (etwa die Firma einer gescannten Visitenkarte). Für sie
// gibt es beim Anlegen der Person noch keine Id: Der Kanal wird deshalb mit
// `companyId: null` und der Firma im Label gespeichert („Geschäftlich
// (WIN.DN GmbH)"). Sobald die Firma angelegt ist, trägt dieser Schritt die Id
// nach — sonst fehlte der Kanal später in der Firmenakte unter den Menschen
// im Unternehmen.

import { apiUrl } from "./api";

export type KommZeile = {
  id?: number;
  kind?: string | null;
  companyId?: number | null;
  /** Alle Unternehmen des Wegs (API, seit 02.10.2026). */
  companyIds?: number[] | null;
  label?: string | null;
};

/** Die Namen in der Klammer am Label-Ende: „Geschäftlich (A, B)" → ["a, b", "a", "b"]. */
const namenImLabel = (label: unknown): string[] => {
  const m = String(label ?? "").match(/\(([^)]+)\)\s*$/);
  if (!m) return [];
  // Der ganze Inhalt zählt mit — ein Firmenname darf selbst ein Komma tragen.
  const inhalt = m[1].trim().toLowerCase();
  return [inhalt, ...inhalt.split(",").map((n) => n.trim())].filter(Boolean);
};

/**
 * Nennt das Label die Firma? Über die Liste in der Klammer — und wie bisher
 * über „(Name)" im Text, damit ein Name mit eigener Klammer („Müller (Holding)
 * GmbH") weiter gefunden wird.
 */
const nenntFirma = (label: unknown, name: string): boolean =>
  namenImLabel(label).includes(name) || String(label ?? "").toLowerCase().includes(`(${name})`);

/** Wie viele Firmen nennt die Klammer am Label-Ende? */
const anzahlImLabel = (label: unknown): number => Math.max(0, namenImLabel(label).length - 1);

const firmenDerZeile = (z: KommZeile): number[] =>
  Array.isArray(z.companyIds)
    ? z.companyIds.map(Number).filter((n) => n > 0)
    : z.companyId != null && Number(z.companyId) > 0
      ? [Number(z.companyId)]
      : [];

/**
 * Welche geschäftlichen Zeilen nennen die Firma im Label, gehören ihr aber noch
 * nicht? Liefert je Zeile die neue Firmenliste (bisherige + diese Firma) —
 * seit 02.10.2026 kann ein Weg mehreren Unternehmen gehören, die übrigen
 * bleiben also stehen.
 */
export function zeilenOhneFirmenId(
  zeilen: KommZeile[],
  firma: { id: number; name: string },
): Array<{ id: number; companyIds: number[] }> {
  const name = firma.name.trim().toLowerCase();
  if (!name) return [];
  return zeilen
    .filter(
      (z) =>
        Number(z.id) > 0 &&
        z.kind === "geschaeftlich" &&
        !firmenDerZeile(z).includes(firma.id) &&
        nenntFirma(z.label, name) &&
        // Nur wenn das Label MEHR Firmen nennt, als die Zeile Ids trägt — sonst
        // gehört der Name schon zu einer zugeordneten Firma.
        Math.max(1, anzahlImLabel(z.label)) > firmenDerZeile(z).length,
    )
    .map((z) => ({ id: Number(z.id), companyIds: [...firmenDerZeile(z), firma.id] }));
}

/** Trägt die Firmen-Id nach. Liefert Fehlertexte (leer = alles gut). */
export async function firmenbezugNachreichen(
  personId: number,
  firma: { id: number; name: string },
): Promise<string[]> {
  try {
    const res = await fetch(apiUrl(`contact/${personId}`, { include: "communications" }));
    if (!res.ok) return [];
    const kontakt = await res.json();
    const zeilen: KommZeile[] = Array.isArray(kontakt?.communications) ? kontakt.communications : [];
    const fehler: string[] = [];
    for (const z of zeilenOhneFirmenId(zeilen, firma)) {
      const put = await fetch(apiUrl(`contact/${personId}/communication/${z.id}`), {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ kind: "geschaeftlich", companyIds: z.companyIds }),
      }).catch(() => null);
      if (!put?.ok) fehler.push(`Ein geschäftlicher Kanal konnte „${firma.name}" nicht zugeordnet werden.`);
    }
    return fehler;
  } catch {
    return [];
  }
}
